import { useCallback, useState, type ComponentProps, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { SheetContent } from '@/components/ui/sheet'

type OutsideEvent = Parameters<NonNullable<ComponentProps<typeof SheetContent>['onInteractOutside']>>[0]

/**
 * Stops an editor sheet from throwing away unsaved work. Closing it (Esc, the X,
 * Cancel or a click outside) asks first while `dirty`; a clean sheet closes as usual.
 * Spread `contentProps` on the SheetContent, route every close through `requestClose`,
 * and render `dialog` once.
 */
export function useDiscardGuard(dirty: boolean, onOpenChange: (open: boolean) => void): {
    requestClose: (open: boolean) => void
    contentProps: { onInteractOutside: (e: OutsideEvent) => void }
    dialog: ReactNode
} {
    const [asking, setAsking] = useState(false)

    const requestClose = useCallback((open: boolean) => {
        if (!open && dirty) setAsking(true)
        else onOpenChange(open)
    }, [dirty, onOpenChange])

    const contentProps = {
        onInteractOutside: (e: OutsideEvent) => {
            if (!dirty) return
            e.preventDefault()
            setAsking(true)
        },
    }

    const dialog = (
        <Dialog open={asking} onOpenChange={setAsking}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Discard your changes?</DialogTitle>
                    <DialogDescription>You have unsaved changes. If you close this now they will be lost.</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setAsking(false)}>Keep editing</Button>
                    <Button type="button" variant="destructive" onClick={() => { setAsking(false); onOpenChange(false) }}>Discard</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
    return { requestClose, contentProps, dialog }
}
